export type Language = 'en' | 'de';

export interface Translations {
  // Navigation & Language & Theme
  langEn: string;
  langDe: string;
  selectLanguage: string;
  navReleases: string;
  navSoundCloud: string;
  navBandcamp: string;
  navYouTube: string;
  navTelegram: string;
  toggleThemeDark: string;
  toggleThemeLight: string;
  menuOpen: string;
  menuClose: string;

  // Welcome Hero
  welcomeGreeting: string;
  welcomeSubtitle: string;
  welcomeScrollPrompt: string;

  // Releases Section
  releasesTitle: string;
  releasesSubtitle: string;
  viewReleaseDetails: string;

  // Release Detail Page
  backToReleases: string;
  detailScrollDownClose: string;
  tracklistTitle: string;
  streamingAndPurchase: string;
  playTrackAction: string;

  // SoundCloud Showcase
  scSectionLabel: string;
  scSectionTitle: string;
  scSectionSubtitle: string;
  scFilterAll: string;
  scFilterAlbum: string;
  scFilterSingle: string;
  scLoadMore: string;
  scPlayTrack: string;
  scCurrentlyPlaying: string;

  // Bandcamp Section
  bcSectionLabel: string;
  bcSectionTitle: string;
  bcSectionSubtitle: string;
  bcCtaButton: string;
  bcLoading: string;

  // YouTube Section
  ytSectionLabel: string;
  ytSectionTitle: string;
  ytSectionSubtitle: string;
  ytConsentNotice: string;
  ytCtaButton: string;
  ytLoading: string;

  // Telegram Section
  tgSectionLabel: string;
  tgSectionTitle: string;
  tgSectionSubtitle: string;
  tgCtaButton: string;
  tgLoading: string;

  // Audio Player
  playerNextUp: string;
  playerAutoPlay: string;
  playerAutoPlayOn: string;
  playerAutoPlayOff: string;
  playerQueueEmpty: string;
  playerCollapse: string;
  playerExpand: string;
  playerPreviousTrack: string;
  playerNextTrack: string;

  // Consent & Privacy Modal
  consentTag: string;
  consentTitle: string;
  consentDescription: string;
  consentCustomizeShow: string;
  consentCustomizeHide: string;
  consentEssentialTitle: string;
  consentEssentialDesc: string;
  consentSoundCloudTitle: string;
  consentSoundCloudDesc: string;
  consentYouTubeTitle: string;
  consentYouTubeDesc: string;
  consentAcceptAll: string;
  consentSaveSelection: string;
  consentEssentialOnly: string;
  consentPrivacyLink: string;

  // SoundCloud Permission Prompt
  scPromptTag: string;
  scPromptTitle: string;
  scPromptDescription: string;
  scPromptAllowBtn: string;
  scPromptCancelBtn: string;

  // Footer
  footerCopyright: string;
  footerImprint: string;
  footerPrivacy: string;
  footerPrivacySettings: string;
}

export const translations: Record<Language, Translations> = {
  en: {
    // Navigation & Language & Theme
    langEn: 'EN',
    langDe: 'DE',
    selectLanguage: 'Switch language',
    navReleases: 'Releases',
    navSoundCloud: 'SoundCloud',
    navBandcamp: 'Bandcamp',
    navYouTube: 'Visuals',
    navTelegram: 'Lyrics & Poems',
    toggleThemeDark: 'Switch to Dark Mode',
    toggleThemeLight: 'Switch to Light Mode',
    menuOpen: 'Open menu',
    menuClose: 'Close menu',

    // Welcome Hero
    welcomeGreeting: 'this is joev noise',
    welcomeSubtitle: 'Electronic music producer, live performer & sound artist',
    welcomeScrollPrompt: 'Scroll down to explore releases',

    // Releases Section
    releasesTitle: 'Discography & Releases',
    releasesSubtitle: 'Selected studio releases, EPs, and albums across electronic styles',
    viewReleaseDetails: 'View release details',

    // Release Detail Page
    backToReleases: '← Back to Releases Overview',
    detailScrollDownClose: '↓ Scroll down to return to releases',
    tracklistTitle: 'Tracklist',
    streamingAndPurchase: 'Streaming & Purchase Links:',
    playTrackAction: 'Play track',

    // SoundCloud Showcase
    scSectionLabel: 'Stream & Listen',
    scSectionTitle: 'SoundCloud Showcase',
    scSectionSubtitle: 'Official albums, EPs, live performances and studio sets on SoundCloud.',
    scFilterAll: 'All',
    scFilterAlbum: 'Album / EP',
    scFilterSingle: 'Single / Track',
    scLoadMore: 'Load More Tracks',
    scPlayTrack: 'Play',
    scCurrentlyPlaying: 'Currently playing',

    // Bandcamp Section
    bcSectionLabel: 'buy & support',
    bcSectionTitle: 'Bandcamp Catalog',
    bcSectionSubtitle:
      'Direct artist support — lossless downloads and official discography releases',
    bcCtaButton: 'Stream & Buy on Bandcamp',
    bcLoading: 'Loading Bandcamp releases...',

    // YouTube Section
    ytSectionLabel: 'watch & listen',
    ytSectionTitle: 'YouTube Visuals & Sessions',
    ytSectionSubtitle: 'Live studio recordings, synthesizer visualizers, and music videos',
    ytConsentNotice: 'Click to load YouTube Video & enable embed',
    ytCtaButton: 'Watch on YouTube →',
    ytLoading: 'Loading videos...',

    // Telegram Section
    tgSectionLabel: 'words & thoughts',
    tgSectionTitle: 'Telegram Writing & Lyrics',
    tgSectionSubtitle: 'Fragments, poetry, and lyrical reflections published via @joevnoise',
    tgCtaButton: 'Open on Telegram channel →',
    tgLoading: 'Loading writing...',

    // Audio Player
    playerNextUp: 'Next Up',
    playerAutoPlay: 'Auto-play',
    playerAutoPlayOn: 'Auto-play ON',
    playerAutoPlayOff: 'Auto-play OFF',
    playerQueueEmpty: 'No upcoming tracks in queue',
    playerCollapse: 'Collapse player',
    playerExpand: 'Expand player',
    playerPreviousTrack: 'Previous track',
    playerNextTrack: 'Next track',

    // Consent & Privacy Modal
    consentTag: 'Privacy & Cookie Preferences',
    consentTitle: 'External Media & Audio Experience',
    consentDescription:
      'This website includes embedded sound streams and video content from third-party services. All fonts are 100% self-hosted locally. To enable background music playback and interactive streaming across the portfolio, please choose your preferences per GDPR / TTDSG:',
    consentCustomizeShow: '▶ Customize Providers & Details',
    consentCustomizeHide: '▼ Hide Provider Settings',
    consentEssentialTitle: 'Essential & Self-Hosted Core (Always Active)',
    consentEssentialDesc:
      'Local storage for preferences, self-hosted fonts & essential portfolio navigation.',
    consentSoundCloudTitle: 'SoundCloud Audio Streaming',
    consentSoundCloudDesc: 'Enables SoundCloud player widget and direct music preview playback.',
    consentYouTubeTitle: 'YouTube Visuals & Sessions',
    consentYouTubeDesc: 'Enables embedded live synthesizer sessions and music visualizers.',
    consentAcceptAll: 'Accept All & Enable Audio',
    consentSaveSelection: 'Save Selection',
    consentEssentialOnly: 'Essential Only',
    consentPrivacyLink: 'Read our Privacy Policy & Cookie Information (Datenschutzerklärung)',

    // SoundCloud Permission Prompt
    scPromptTag: 'Audio Stream Consent',
    scPromptTitle: 'Enable SoundCloud Audio Playback',
    scPromptDescription:
      'You previously chose Essential-Only mode. To stream and play audio from SoundCloud, please confirm your permission to connect to SoundCloud servers:',
    scPromptAllowBtn: 'Allow & Play Track',
    scPromptCancelBtn: 'Cancel',

    // Footer
    footerCopyright: 'by Fabian Althaus, alias Joev Noise. All rights reserved.',
    footerImprint: 'Legal Notice / Impressum',
    footerPrivacy: 'Privacy Policy / Datenschutz',
    footerPrivacySettings: 'Privacy Settings',
  },
  de: {
    // Navigation & Language & Theme
    langEn: 'EN',
    langDe: 'DE',
    selectLanguage: 'Sprache wechseln',
    navReleases: 'Releases',
    navSoundCloud: 'SoundCloud',
    navBandcamp: 'Bandcamp',
    navYouTube: 'Videos',
    navTelegram: 'Texte & Lyrik',
    toggleThemeDark: 'Zum Dunkelmodus wechseln',
    toggleThemeLight: 'Zum Hellmodus wechseln',
    menuOpen: 'Menü öffnen',
    menuClose: 'Menü schließen',

    // Welcome Hero
    welcomeGreeting: 'das ist joev noise',
    welcomeSubtitle: 'Elektronischer Musikproduzent, Live-Performer & Klangkünstler',
    welcomeScrollPrompt: 'Nach unten scrollen zum Entdecken',

    // Releases Section
    releasesTitle: 'Diskografie & Veröffentlichungen',
    releasesSubtitle:
      'Ausgewählte Studio-Releases, EPs und Alben verschiedener elektronischer Stile',
    viewReleaseDetails: 'Veröffentlichungsdetails ansehen',

    // Release Detail Page
    backToReleases: '← Zurück zur Veröffentlichungs-Übersicht',
    detailScrollDownClose: '↓ Nach unten scrollen, um zur Übersicht zurückzukehren',
    tracklistTitle: 'Titelliste',
    streamingAndPurchase: 'Streaming- & Kauf-Links:',
    playTrackAction: 'Titel abspielen',

    // SoundCloud Showcase
    scSectionLabel: 'Streamen & Hören',
    scSectionTitle: 'SoundCloud Showcase',
    scSectionSubtitle: 'Offizielle Alben, EPs, Live-Performances und Studio-Sets auf SoundCloud.',
    scFilterAll: 'Alle',
    scFilterAlbum: 'Album / EP',
    scFilterSingle: 'Single / Titel',
    scLoadMore: 'Weitere Titel laden',
    scPlayTrack: 'Abspielen',
    scCurrentlyPlaying: 'Wird gerade abgespielt',

    // Bandcamp Section
    bcSectionLabel: 'kaufen & unterstützen',
    bcSectionTitle: 'Bandcamp Katalog',
    bcSectionSubtitle:
      'Direkte Künstlerunterstützung — verlustfreie Downloads und offizielle Diskografie',
    bcCtaButton: 'Auf Bandcamp streamen & kaufen',
    bcLoading: 'Bandcamp-Releases werden geladen...',

    // YouTube Section
    ytSectionLabel: 'sehen & hören',
    ytSectionTitle: 'YouTube Visuals & Sessions',
    ytSectionSubtitle: 'Live-Studioaufnahmen, Synthesizer-Visualizer und Musikvideos',
    ytConsentNotice: 'Klicken zum Laden des YouTube-Videos & Aktivieren des Embeds',
    ytCtaButton: 'Auf YouTube ansehen →',
    ytLoading: 'Videos werden geladen...',

    // Telegram Section
    tgSectionLabel: 'worte & gedanken',
    tgSectionTitle: 'Telegram Texte & Lyrik',
    tgSectionSubtitle: 'Fragmente, Poesie und lyrische Gedanken veröffentlicht via @joevnoise',
    tgCtaButton: 'Auf Telegram-Kanal öffnen →',
    tgLoading: 'Texte werden geladen...',

    // Audio Player
    playerNextUp: 'Als Nächstes',
    playerAutoPlay: 'Automatisch',
    playerAutoPlayOn: 'Auto-Play AN',
    playerAutoPlayOff: 'Auto-Play AUS',
    playerQueueEmpty: 'Keine weiteren Titel in der Warteschlange',
    playerCollapse: 'Player minimieren',
    playerExpand: 'Player maximieren',
    playerPreviousTrack: 'Vorheriger Titel',
    playerNextTrack: 'Nächster Titel',

    // Consent & Privacy Modal
    consentTag: 'Datenschutz- & Cookie-Einstellungen',
    consentTitle: 'Externe Medien & Audio-Erlebnis',
    consentDescription:
      'Diese Website bindet externe Audio-Streams und Videoinhalte von Drittanbietern ein. Alle Schriftarten werden zu 100% lokal gehostet. Um Musik-Streaming und interaktive Wiedergabe im Portfolio zu aktivieren, wähle bitte deine Einstellungen gemäß DSGVO / TTDSG:',
    consentCustomizeShow: '▶ Anbieter anpassen & Details',
    consentCustomizeHide: '▼ Einstellungen verbergen',
    consentEssentialTitle: 'Essenzieller & lokal gehosteter Kern (Immer aktiv)',
    consentEssentialDesc:
      'Lokale Speicherung für Einstellungen, lokale Schriftarten & grundlegende Navigation.',
    consentSoundCloudTitle: 'SoundCloud Audio-Streaming',
    consentSoundCloudDesc: 'Aktiviert das SoundCloud-Player-Widget und direkte Musikwiedergabe.',
    consentYouTubeTitle: 'YouTube Visuals & Sessions',
    consentYouTubeDesc: 'Aktiviert eingebettete Synthesizer-Live-Sessions und Musik-Visualizer.',
    consentAcceptAll: 'Alle akzeptieren & Audio aktivieren',
    consentSaveSelection: 'Auswahl speichern',
    consentEssentialOnly: 'Nur essenzielle',
    consentPrivacyLink: 'Unsere Datenschutzerklärung & Cookie-Informationen lesen',

    // SoundCloud Permission Prompt
    scPromptTag: 'Audio-Stream Einwilligung',
    scPromptTitle: 'SoundCloud Audio-Wiedergabe aktivieren',
    scPromptDescription:
      'Du hast zuvor den Modus "Nur essenzielle" gewählt. Um Audio von SoundCloud zu streamen, bestätige bitte die Verbindung zu den SoundCloud-Servern:',
    scPromptAllowBtn: 'Erlauben & Titel abspielen',
    scPromptCancelBtn: 'Abbrechen',

    // Footer
    footerCopyright: 'von Fabian Althaus, alias Joev Noise. Alle Rechte vorbehalten.',
    footerImprint: 'Impressum / Rechtliche Hinweise',
    footerPrivacy: 'Datenschutzerklärung',
    footerPrivacySettings: 'Datenschutzeinstellungen',
  },
};
